import { useState, type ChangeEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUp, Layers3 } from "lucide-react";
import type { ShortLink } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { Button, Dialog, Empty, Field, Tag, useToast } from "./ui";

function parseUrls(value: string): string[] {
  return [...new Set(value.split(/[\r\n,]+/).map((item) => item.trim()).filter(Boolean))];
}

export function BatchCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const [expiryMode, setExpiryMode] = useState<"default" | "30d" | "90d">("default");
  const [results, setResults] = useState<ShortLink[]>([]);
  const urls = parseUrls(value);
  const mutation = useMutation({
    mutationFn: () =>
      api<ShortLink[]>("/api/links/batch", {
        method: "POST",
        ...jsonBody({ urls, expiryMode }),
      }),
    async onSuccess(items) {
      setResults(items);
      toast.push(`已生成 ${items.length} 条短链`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["links"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
    onError: toast.fromError,
  });

  const importFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") setValue(reader.result);
    });
    reader.readAsText(file);
  };

  const close = () => {
    setResults([]);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} title="批量生成" eyebrow="BATCH · MAX 200" wide>
      {results.length > 0 ? (
        <div className="batch-results">
          <header><Tag tone="accent">{results.length} CREATED</Tag><p>命中审核规则的链接会保持待审，其余已立即生效。</p></header>
          <div className="batch-results__list">
            {results.map((link) => (
              <div key={link.id}><strong>{link.shortUrl}</strong><span>{link.targetUrl}</span><Tag tone={link.status === "active" ? "neutral" : "accent"}>{link.status === "active" ? "生效" : "待审"}</Tag></div>
            ))}
          </div>
          <div className="dialog-actions"><Button variant="primary" onClick={() => setResults([])}>继续生成</Button><Button onClick={close}>完成</Button></div>
        </div>
      ) : (
        <form
          className="batch-form"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="batch-channels">
            <div className="batch-channel batch-channel--active"><Layers3 size={18} /><strong>粘贴列表</strong><span>每行一个 URL</span></div>
            <label className="batch-channel batch-channel--upload"><FileUp size={18} /><strong>导入 CSV</strong><span>读取首列或逗号分隔</span><input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={importFile} /></label>
          </div>
          <Field label={`目标网址 · ${urls.length} / 200`} hint="会自动去重；整批写入具有原子性，任一失败时不会留下半批数据。">
            <textarea className="input textarea textarea--batch" value={value} onChange={(event) => setValue(event.target.value)} placeholder={"https://example.com/article-one\nhttps://example.com/article-two"} autoFocus />
          </Field>
          <Field label="统一有效期 · EXPIRY">
            <select className="input select" value={expiryMode} onChange={(event) => setExpiryMode(event.target.value as typeof expiryMode)}>
              <option value="default">系统默认</option>
              <option value="30d">30 天</option>
              <option value="90d">90 天</option>
            </select>
          </Field>
          {urls.length === 0 ? <Empty title="等待网址" body="粘贴列表或选择 CSV 文件，系统会在提交前显示条目数。" /> : null}
          <div className="dialog-actions"><Button onClick={close}>取消</Button><Button variant="primary" type="submit" disabled={urls.length === 0 || urls.length > 200 || mutation.isPending}><Layers3 size={15} />{mutation.isPending ? "正在生成…" : `生成 ${urls.length} 条`}</Button></div>
        </form>
      )}
    </Dialog>
  );
}

